import {
  PaymentOption,
  QuoteStatus,
  type InventoryOrderPlaceResultRecord,
  type TireQuoteRecordCustomer,
  type TireQuoteRecordList,
  type TireQuoteRecordPartner
} from '@navio/nsdk';

import type { TireOfferMinimal } from 'noddi-types';

type TireQuoteRecordLike = TireQuoteRecordPartner | TireQuoteRecordCustomer | TireQuoteRecordList;

// The backend's QuoteStatus labels are sentences, too long for a status line or badge.
export const TIRE_OFFER_STATUS_LABELS: Record<QuoteStatus, string> = {
  [QuoteStatus.DRAFT]: 'Draft',
  [QuoteStatus.PENDING]: 'Sent to customer',
  [QuoteStatus.ACCEPTED]: 'Accepted',
  [QuoteStatus.REJECTED]: 'Declined',
  [QuoteStatus.WITHDRAWN]: 'Withdrawn',
  [QuoteStatus.CANCELLED]: 'Canceled',
  [QuoteStatus.PARTIALLY_REFUNDED]: 'Partly refunded',
  [QuoteStatus.REFUNDED]: 'Refunded',
  [QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL]: 'Tires ordered',
  [QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL]: 'Tires partly ordered',
  [QuoteStatus.INVENTORY_RECEIVED_IN_FULL]: 'Tires received',
  [QuoteStatus.INVENTORY_RECEIVED_PARTIAL]: 'Tires partly received',
  [QuoteStatus.TIRES_RECEIVED_AWAITING_MOUNTING_ON_RIM_AT_STORAGE_FACILITY]: 'Awaiting rim mounting',
  [QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY]: 'Mounted on rims',
  [QuoteStatus.BOOKING_PROPOSAL_TIRE_MOUNT_SENT_TO_CUSTOMER]: 'Mounting offered',
  [QuoteStatus.TIRE_MOUNTING_BOOKED]: 'Mounting booked',
  [QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER]: 'Mounted at customer',
  [QuoteStatus.FULFILLED]: 'Fulfilled',
  [QuoteStatus.FULFILLED_PARTIALLY]: 'Partly fulfilled',
  [QuoteStatus.TO_BE_RETURNED_TO_SUPPLIER]: 'Return to supplier'
};

type SupplierOrderQuote = {
  isPaid: boolean;
  isSelected: boolean;
  paymentMethod: PaymentOption;
};

// Accepted invoice and pay-after quotes can be ordered before a captured payment.
const DEFERRED_SUPPLIER_ORDER_PAYMENTS = new Set<PaymentOption>([PaymentOption.INVOICE, PaymentOption.PAY_AFTER]);

export function hasInventoryOrders(tireOffer: TireOfferMinimal | TireQuoteRecordLike) {
  if ('hasInventoryOrders' in tireOffer) {
    return tireOffer.hasInventoryOrders;
  }
  return tireOffer.inventoryOrders.length > 0;
}

export function tireQuoteRequiresUnpaidOrderApproval(tireOffer: SupplierOrderQuote) {
  return tireOffer.isSelected && !tireOffer.isPaid && DEFERRED_SUPPLIER_ORDER_PAYMENTS.has(tireOffer.paymentMethod);
}

export function tireQuoteNeedsSupplierOrder(tireOffer: (TireOfferMinimal | TireQuoteRecordLike) & SupplierOrderQuote) {
  if (!tireOffer.isSelected || hasInventoryOrders(tireOffer)) {
    return false;
  }
  return tireOffer.isPaid || DEFERRED_SUPPLIER_ORDER_PAYMENTS.has(tireOffer.paymentMethod);
}

export function unpaidSupplierOrderApprovalDescription(paymentMethod: PaymentOption) {
  if (paymentMethod === PaymentOption.INVOICE) {
    return 'Customer accepted this offer on invoice. Ordering tires before the invoice is paid.';
  }
  if (paymentMethod === PaymentOption.PAY_AFTER) {
    return 'Customer accepted this offer as pay after. Ordering tires before payment.';
  }
  return '';
}

const CANCELED_SUPPLIER_ORDER_STATUSES = new Set<QuoteStatus>([
  QuoteStatus.CANCELLED,
  QuoteStatus.REFUNDED,
  QuoteStatus.PARTIALLY_REFUNDED
]);

const NOT_ACCEPTED_SUPPLIER_ORDER_STATUSES = new Set<QuoteStatus>([
  QuoteStatus.DRAFT,
  QuoteStatus.PENDING,
  QuoteStatus.REJECTED,
  QuoteStatus.WITHDRAWN
]);

// Mirrors the supplier-order gate: canceled first, then not accepted, then unpaid pay-before.
// Invoice and pay-after stay open so the approval note can be collected.
export function automaticSupplierOrderBlockedReason(tireOffer: SupplierOrderQuote & { status?: QuoteStatus | null }) {
  if (tireOffer.status && CANCELED_SUPPLIER_ORDER_STATUSES.has(tireOffer.status)) {
    return 'This offer is canceled, so tires cannot be ordered automatically.';
  }
  if (!tireOffer.isSelected || (tireOffer.status && NOT_ACCEPTED_SUPPLIER_ORDER_STATUSES.has(tireOffer.status))) {
    return 'The customer has not accepted this offer yet.';
  }
  if (!tireOffer.isPaid && !DEFERRED_SUPPLIER_ORDER_PAYMENTS.has(tireOffer.paymentMethod)) {
    return 'This offer is accepted but not paid. Automatic ordering waits until the customer pays. You can still record a manual order.';
  }
  return null;
}

export type InventoryPlaceOutcome = 'placed' | 'withIssues' | 'none';

// A timeout or a skipped line comes back as a 200 with every list empty. That is not a placed order.
export function classifyInventoryPlaceResult(result: InventoryOrderPlaceResultRecord): InventoryPlaceOutcome {
  const created =
    result.inventoryOrdersNew.length + result.inventoryOrdersExisting.length + (result.ownStockItems?.length ?? 0);
  const problems = result.unavailableItems.length + (result.partialItems?.length ?? 0);
  if (created === 0 && problems === 0) {
    return 'none';
  }
  if (problems > 0) {
    return 'withIssues';
  }
  return 'placed';
}
